/**
 * FloatingSaveButton
 * ─────────────────────────────────────────────────────────────
 * FAB (Floating Action Button) para formularios largos con scroll.
 * Se ancla al viewport (position: fixed) y se mantiene visible mientras
 * el usuario desplaza la página. Reemplaza al patrón anterior de tener
 * un botón Guardar en el header + otro al pie.
 *
 * · Posición: bottom-right (coordinada con AssistantWidget que va en
 *   bottom-24 right-5, así este va en bottom-6 right-5).
 * · Estado saving muestra Loader animado y texto "Guardando…".
 * · Estado disabled y saving se combinan (no permite reenvío mientras
 *   está guardando).
 * · Atajo de teclado: Ctrl+S / ⌘+S dispara onClick automáticamente
 *   (para power users que hacen muchas ediciones al día).
 *
 * Props:
 *   · onClick   (fn)      requerido — se llama al hacer click / Ctrl+S.
 *   · saving    (bool)    opcional — muestra Loader y bloquea el botón.
 *   · disabled  (bool)    opcional — bloquea el botón (ej. formulario inválido).
 *   · label     (string)  opcional — texto del botón (default "Guardar").
 *   · savingLabel (string) opcional — texto durante saving (default "Guardando…").
 *   · testid    (string)  opcional — data-testid único por pantalla.
 *   · hidden    (bool)    opcional — para ocultarlo condicionalmente
 *     (ej. cuando el usuario está en una tab sin cambios pendientes).
 */
import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Save, Loader2 } from "lucide-react";

export function FloatingSaveButton({
  onClick,
  saving = false,
  disabled = false,
  label = "Guardar",
  savingLabel = "Guardando…",
  testid = "floating-save",
  hidden = false,
}) {
  // Atajo Ctrl+S / ⌘+S — evita el "Save page as" del navegador y dispara el guardado.
  useEffect(() => {
    if (hidden) return undefined;
    const onKey = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      if (isSave && !saving && !disabled) {
        e.preventDefault();
        onClick?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClick, saving, disabled, hidden]);

  if (hidden) return null;

  return (
    <Button
      onClick={onClick}
      disabled={disabled || saving}
      data-testid={testid}
      className="fixed bottom-6 right-5 z-40 h-12 gap-2 rounded-full px-5 shadow-lg shadow-primary/25 hover:shadow-primary/40"
      title={`${label} (Ctrl+S)`}
    >
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
      <span className="text-sm font-semibold">{saving ? savingLabel : label}</span>
    </Button>
  );
}

export default FloatingSaveButton;
